const resolveLocationId = (filters) => {
  if (!filters || filters.locationId === null || filters.locationId === undefined || filters.locationId === '') {
    return null;
  }
  const parsed = parseInt(filters.locationId, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const idsMatch = (a, b) => {
  if (a === null || a === undefined || b === null || b === undefined || a === '' || b === '') {
    return false;
  }
  const left = parseInt(a, 10);
  const right = parseInt(b, 10);
  if (Number.isNaN(left) || Number.isNaN(right)) return false;
  return left === right;
};

const resolveDisplayName = (ind, ctx) => {
  let displayName = (ind && (ind.full_name || ind.name)) || (ctx && ctx.indicatorName) || 'indicator';
  if (displayName === 'undefined' || !displayName) {
    displayName = 'Measured Indicator';
  }
  return displayName;
};

/**
 * Prefer two different reporting periods from newest-first results.
 */
const pickPeriodPair = (results) => {
  if (!Array.isArray(results) || results.length === 0) return null;

  const current = results[0];
  const previous = results.find((row) => String(row.period) !== String(current.period));

  if (previous) {
    return { current, previous };
  }

  return { current, previous: null };
};

const buildDataQueryParams = (indicatorId, filters, options = {}) => {
  const {
    includeLocation = true,
    includeDatasource = true,
    size = 20,
  } = options;

  const params = {
    indicator: indicatorId,
    ordering: '-period',
    size,
  };

  if (includeLocation) {
    const locationId = resolveLocationId(filters);
    if (locationId) params.location = locationId;
  }

  if (includeDatasource && filters && filters.datasourceId) {
    params.datasource = filters.datasourceId;
  }

  return params;
};

const buildInsightFromIndicator = (ind, current, previous, context) => {
  const val = parseFloat(current.value);
  const prevVal = parseFloat(previous.value);
  const ctx = context || {};

  if (Number.isNaN(val) || Number.isNaN(prevVal)) return null;

  let percentChange = 0;
  if (prevVal === 0 && val > 0) {
    percentChange = 1;
  } else if (prevVal === 0 && val === 0) {
    percentChange = 0;
  } else {
    percentChange = (val - prevVal) / prevVal;
  }

  const absChange = Math.abs(percentChange);
  const displayName = resolveDisplayName(ind, ctx);
  const formattedPercent = Math.round(absChange * 100);
  const sourceNote = ctx.datasource && ctx.datasource !== 'N/A' ? ` Data source: ${ctx.datasource}.` : '';
  let type = 'positive';
  let priorityScore = 0;
  let title = '';
  let description = '';
  let recommendation = '';

  if (percentChange > 0.3) {
    type = 'critical';
    title = `Significant Spike: ${displayName}`;
    description = `Observed a sharp ${formattedPercent}% increase in ${displayName} (${previous.period} to ${current.period}).${sourceNote}`;
    recommendation = 'Investigate the cause of this sudden surge. Verify data reporting quality and allocate necessary resources if this represents an increased load on health facilities.';
    priorityScore = Math.min(Math.round(absChange * 100), 100);
  } else if (percentChange < -0.3) {
    type = 'critical';
    title = `Critical Drop: ${displayName}`;
    description = `${displayName} decreased by ${formattedPercent}% from ${prevVal} (${previous.period}) to ${val} (${current.period}).${sourceNote}`;
    recommendation = 'Urgent alert: Ensure there are no stock-outs, reporting gaps, or external factors suppressing this indicator. Cross-check with regional M&E officers immediately.';
    priorityScore = Math.min(Math.round(absChange * 100), 100);
  } else if (absChange > 0.1) {
    type = 'warning';
    title = `Trend Shift: ${displayName}`;
    description = `Noticeable ${formattedPercent}% change in ${displayName} between ${previous.period} and ${current.period}.${sourceNote}`;
    recommendation = 'Keep an eye on this trend over the next reporting cycle to rule out early signs of program disruption.';
    priorityScore = 50 + Math.round(absChange * 20);
  } else {
    type = 'positive';
    title = `Stable: ${displayName}`;
    description = `${displayName} is stable with ${formattedPercent}% variance between ${previous.period} and ${current.period}.${sourceNote}`;
    recommendation = 'Standard operations proceeding as expected. No immediate intervention required.';
    priorityScore = 25;
  }

  return {
    id: ind.id,
    title,
    description,
    recommendation,
    state: current.location_name || ctx.locationName || 'National',
    indicator: displayName,
    priority_score: priorityScore,
    type,
    is_selected: idsMatch(ind.id, ctx.selectedIndicatorId),
  };
};

/**
 * Fallback when only one usable period exists for the selected view.
 */
const buildLimitedInsightFromIndicator = (ind, current, context) => {
  const ctx = context || {};
  const displayName = resolveDisplayName(ind, ctx);
  const val = parseFloat(current && current.value);
  const valueLabel = Number.isNaN(val) ? 'an available reading' : String(val);
  const periodLabel = (current && current.period) || 'the latest period';
  const sourceNote = ctx.datasource && ctx.datasource !== 'N/A' ? ` Data source: ${ctx.datasource}.` : '';
  const isSelected = idsMatch(ind.id, ctx.selectedIndicatorId);

  return {
    id: ind.id,
    title: `Current reading: ${displayName}`,
    description: `${displayName} is ${valueLabel} for ${periodLabel}.${sourceNote} Not enough prior periods were available for a full change comparison in this view.`,
    recommendation: 'Review related periods or widen location/source filters if you need a period-over-period comparison. The current reading is still useful for situational awareness.',
    state: (current && current.location_name) || ctx.locationName || 'National',
    indicator: displayName,
    priority_score: isSelected ? 55 : 30,
    type: 'warning',
    is_selected: isSelected,
  };
};

const sortInsights = (insights, selectedIndicatorId) => {
  const selectedId = selectedIndicatorId ? parseInt(selectedIndicatorId, 10) : null;
  const list = Array.isArray(insights) ? [...insights] : [];

  return list.sort((a, b) => {
    if (selectedId && idsMatch(a.id, selectedId) && !idsMatch(b.id, selectedId)) return -1;
    if (selectedId && idsMatch(b.id, selectedId) && !idsMatch(a.id, selectedId)) return 1;
    return b.priority_score - a.priority_score;
  }).slice(0, 5);
};

export {
  resolveLocationId,
  idsMatch,
  pickPeriodPair,
  buildInsightFromIndicator,
  buildLimitedInsightFromIndicator,
  sortInsights,
  buildDataQueryParams,
};
